import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Avatar,
    CircularProgress,
    IconButton,
    Divider,
} from '@mui/material';
import { MdContentCopy, MdOpenInNew, MdCheckCircle } from 'react-icons/md';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { closeModal, showSnackbar } from '../../../redux/slice/AppSlice';
import api from '../../../config/interceptor';
import StripePayoutModal from './StripePayoutModal';

/**
 * Payment Method Configuration
 * Add new payment methods here easily
 */
const PAYMENT_METHOD_CONFIG = {
    'PayPal': {
        label: 'PayPal',
        color: '#0070BA',
        accountLabel: 'PayPal Email',
        linkPrefix: 'https://paypal.me/',
        instructions: 'Please process this payment manually using PayPal. After completing the transfer, click "Mark as Paid" to update the status.',
        buttonText: 'Mark as Paid',
        showAmount: true,
        isAutomatic: false, // Manual payment
    },
    'Cash App': {
        label: 'Cash App',
        color: '#00D54B',
        accountLabel: 'Cash App $Tag',
        linkPrefix: 'https://cash.app/',
        instructions: 'Please process this payment manually using Cash App. After completing the transfer, click "Mark as Paid" to update the status.',
        buttonText: 'Mark as Paid',
        showAmount: true,
        isAutomatic: false,
    },
    'Venmo': {
        label: 'Venmo',
        color: '#008CFF',
        accountLabel: 'Venmo Username',
        linkPrefix: 'https://venmo.com/',
        instructions: 'Please process this payment manually using Venmo. After completing the transfer, click "Mark as Paid" to update the status.',
        buttonText: 'Mark as Paid',
        showAmount: true,
        isAutomatic: false,
    },
    'Zelle': {
        label: 'Zelle',
        color: '#6D1ED4',
        accountLabel: 'Zelle Email/Phone',
        instructions: 'Please process this payment manually using Zelle. After completing the transfer, click "Mark as Paid" to update the status.',
        buttonText: 'Mark as Paid',
        showAmount: true,
        isAutomatic: false,
    },
};

const ProcessPayoutModal = ({ user, onSuccess = () => {} }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [amount, setAmount] = useState('');
    const [copiedField, setCopiedField] = useState(null);

    // Extract payout method details
    const payoutMethod = user?.payout_method || {};
    const {
        type = 'PayPal',
        account_link = '',
        account_info = '',
        attachment = '', // QR code or image
    } = payoutMethod;

    // Get configuration for this payment method
    const config = PAYMENT_METHOD_CONFIG[type] || PAYMENT_METHOD_CONFIG['PayPal'];
    const availableBalance = parseFloat(user?.available_balance || 0);

    // Handle copy to clipboard
    const handleCopy = async (text, field) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            dispatch(showSnackbar({
                message: `${field} copied to clipboard`,
                severity: 'success',
            }));
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Handle open link
    const handleOpenLink = (link) => {
        window.open(link, '_blank', 'noopener,noreferrer');
    };

    // Handle process payment
    const handleProcessPayment = async () => {
        // Validate amount
        const paymentAmount = parseFloat(amount);
        if (!paymentAmount || paymentAmount <= 0) {
            dispatch(showSnackbar({
                message: 'Please enter a valid amount',
                severity: 'error',
            }));
            return;
        }

        if (paymentAmount > availableBalance) {
            dispatch(showSnackbar({
                message: `Amount cannot exceed available balance ($${availableBalance.toFixed(2)})`,
                severity: 'error',
            }));
            return;
        }

        setLoading(true);

        try {
            let endpoint = '';
            let payload = {};

            if (config.isAutomatic) {
                // Automatic payment (Stripe)
                endpoint = '/payout/stripe/create';
                payload = {
                    artist_id: user.id,
                    amount: paymentAmount,
                    currency: 'usd',
                };
            } else {
                // Manual payment (PayPal, Cash App, Venmo, etc.)
                endpoint = '/payout/offline/create';
                payload = {
                    artist_id: user.id,
                    amount: paymentAmount,
                    currency: 'usd',
                    payment_method: type,
                    payment_info: account_info,
                    note: `Manual payout via ${type}`,
                };
            }

            const response = await api.post(endpoint, payload);

            if (response.data?.isSuccess) {
                dispatch(closeModal());
                dispatch(showSnackbar({
                    message: config.isAutomatic 
                        ? 'Payout processed via Stripe successfully' 
                        : 'Payout marked as pending. Please complete the manual transfer.',
                    severity: 'success',
                }));
                onSuccess();
            } else {
                const errorMsg = response.data?.message?.msg || 'Failed to process payout';
                dispatch(showSnackbar({
                    message: errorMsg,
                    severity: 'error',
                }));
            }

        } catch (error) {
            console.error('Payout error:', error);
            const errorMessage = error.response?.data?.message?.msg || error.message || 'Failed to process payout';
            dispatch(showSnackbar({
                message: errorMessage,
                severity: 'error',
            }));
        } finally {
            setLoading(false);
        }
    };

    if(type === "Stripe"){
        return (
            <StripePayoutModal
                user={user}
                onSuccess={onSuccess}
                payoutMethod={payoutMethod}
            />
        )
    }

    return (
        <Box sx={{ width: '100%', minWidth: '400px' }}>
            {/* Header */}
            <Typography
                variant="h6"
                sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.text.primary,
                    mb: 1,
                }}
            >
                Process Payout
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 3 }}
            >
                {user?.first_name} {user?.last_name} - ${availableBalance.toFixed(2)} available
            </Typography>

            {/* User Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                    src={user?.profile_picture || ''}
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.blackColor.black1,
                        fontSize: '1rem',
                        fontWeight: 700,
                    }}
                >
                    {user?.first_name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            fontSize: '1rem',
                        }}
                    >
                        {user?.first_name} {user?.last_name}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {user?.email}
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Payment Method Badge */}
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    borderRadius: 2,
                    backgroundColor: `${config.color}15`,
                    border: `1px solid ${config.color}40`,
                    mb: 2,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        mr: 1,
                    }}
                >
                    Payout Method {config.label}
                </Typography>
                {/* <Typography
                    variant="body2"
                    sx={{ 
                        color: config.color,
                        fontWeight: 700,
                    }}
                >
                    {config.label}
                </Typography> */}
            </Box>

            {/* Amount Input */}
            {config.showAmount && (
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{ 
                            mb: 0.8, 
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                        }}
                    >
                        Payout Amount <span style={{ color: theme.palette.error.main }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setAmount(value);
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <Typography sx={{ mr: 0.5, color: theme.palette.text.primary }}>
                                    $
                                </Typography>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                borderRadius: 2,
                                '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                                },
                                '&:hover fieldset': {
                                    borderColor: config.color,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: config.color,
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                fontSize: '1.1rem',
                            },
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{ 
                            color: theme.palette.text.secondary,
                            display: 'block',
                            mt: 0.5,
                        }}
                    >
                        Maximum: ${availableBalance.toFixed(2)}
                    </Typography>
                </Box>
            )}

            {/* Account Info */}
            {account_info && (
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{ 
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            display: 'block',
                            mb: 0.5,
                        }}
                    >
                        {config.accountLabel}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                    >
                        <Typography
                            sx={{
                                flex: 1,
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                fontFamily: 'monospace',
                            }}
                        >
                            {account_info}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => handleCopy(account_info, config.accountLabel)}
                            sx={{
                                color: copiedField === config.accountLabel 
                                    ? theme.palette.success.main 
                                    : theme.palette.text.secondary,
                            }}
                        >
                            {copiedField === config.accountLabel ? (
                                <MdCheckCircle size={18} />
                            ) : (
                                <MdContentCopy size={18} />
                            )}
                        </IconButton>
                    </Box>
                </Box>
            )}

            {/* Direct Payment Link */}
            {account_link && (
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{ 
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            display: 'block',
                            mb: 0.5,
                        }}
                    >
                        Direct Payment Link
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                    >
                        <Typography
                            sx={{
                                flex: 1,
                                color: theme.palette.text.secondary,//config.color
                                fontWeight: 500,
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {account_link}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => handleCopy(account_link, 'Link')}
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            {copiedField === 'Link' ? (
                                <MdCheckCircle size={18} />
                            ) : (
                                <MdContentCopy size={18} />
                            )}
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleOpenLink(account_link)}
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            <MdOpenInNew size={18} />
                        </IconButton>
                    </Box>
                </Box>
            )}

            {/* QR Code / Attachment */}
            {attachment && (
                <Box sx={{ mb: 2,}}>
                    <Typography
                        variant="caption"
                        sx={{ 
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            display: 'block',
                            mb: 1,
                        }}
                    >
                        QR Code / Payment Info
                    </Typography>
                    <Box
                        component="img"
                        src={attachment}
                        alt="Payment QR Code"
                        sx={{
                            maxWidth: '200px',
                            width: '100%',
                            height: 'auto',
                            borderRadius: 2,
                            border: `2px solid ${config.color}40`,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            padding: 1,
                        }}
                    />
                </Box>
            )}

            {/* Instructions */}
            <Box
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    border: `1px solid ${config.color}30`,
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        display: 'block',
                        mb: 0.5,
                    }}
                >
                    {config.isAutomatic ? 'Automatic Payout:' : 'Manual Payout:'}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ 
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                    }}
                >
                    {config.instructions}
                </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => dispatch(closeModal())}
                    disabled={loading}
                    sx={{
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: theme.palette.text.primary,
                        borderRadius: 2,
                        py: 1.3,
                        fontWeight: 600,
                        '&:hover': {
                            borderColor: 'rgba(255,255,255,0.4)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleProcessPayment}
                    disabled={loading || !amount}
                    startIcon={config.isAutomatic ? undefined : <MdCheckCircle />}
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderRadius: 2,
                        py: 1.3,
                        fontWeight: 700,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                            opacity: 0.9,
                        },
                        '&:disabled': {
                            backgroundColor: `${theme.palette.primary.main}40`,
                            color: theme.palette.primary.contrastText,
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                    ) : (
                        config.buttonText
                    )}
                </Button>
            </Box>
        </Box>
    );
};

export default ProcessPayoutModal;